/**
 * Measurement Alias System — Multi-Vendor DICOM SR Mapping
 *
 * This file is the single source of truth for mapping vendor-specific
 * DICOM SR concept names to canonical measurement short names.
 *
 * When a new vendor/machine doesn't auto-populate a field:
 *   1. Get their SR file
 *   2. Find what their machine calls the measurement
 *   3. Add that string to the appropriate alias array below
 *   4. Deploy — no logic changes needed
 *
 * Vendors covered:
 *   - GE Vivid (E95, E9, E7, S70, S60) — EchoPAC
 *   - Philips EPIQ / Affiniti / iE33 / CX50 — QLAB, IntelliSpace
 *   - Siemens Acuson (SC2000, Sequoia, Juniper) — syngo Dynamics
 *   - Canon/Toshiba Aplio (i800, i900, a450)
 *   - Mindray (Resona 7, Resona I9, DC-70, M9)
 *   - Samsung (RS85, HS70A, V8)
 *   - Hitachi/Fujifilm (ARIETTA 850, Lisendo 880)
 *   - US2.AI (AI-based echo analysis platform) — coding scheme 99IMPROOF
 *   - DICOM Standard (LOINC / SNOMED-CT code meanings from CID 12227-12233)
 *
 * LOINC codes are included in comments for audit trail.
 * The FIRST entry in each array is always the GE name (current production mapping).
 */

// ─── Measurement Concept Name Aliases ─────────────────────────────────────────
// Key   = short name (matches requiredMeasurementsJSON values)
// Value = array of known SR concept names across all vendors
//         The FIRST entry is always the current GE name (existing production mapping)

export const measurementAliases: Record<string, string[]> = {

  // ═══════════════════════════════════════════════════════════════════════════
  // LV Structure & Dimensions
  // ═══════════════════════════════════════════════════════════════════════════

  // LOINC: 18083-6 | Finding Site: T-32300 (Left Ventricle)
  LVIDd: [
    'Left Ventricle Internal End Diastolic Dimension',    // GE Vivid
    'LVIDd',                                               // Philips EPIQ/Affiniti
    'LV Internal Dimension d',                             // Philips alternate
    'Left Ventricular Internal Diameter at End Diastole',  // Philips iE33/CX50 (verbose)
    'LV End-Diastolic Internal Diameter',                  // Siemens Acuson
    'LV Internal Dimension - Diastole',                    // Canon/Toshiba Aplio
    'LVDd',                                                // Mindray / Samsung
    'LV Internal Dimension Diastole',                      // Hitachi/Fujifilm
    'Left Ventricular Internal Diameter End Diastole',     // DICOM standard alternate
    'LV Internal Diameter Diastole',                       // Generic
    'Left Ventricular Internal Diastolic Dimension',       // DICOM CID 12227
  ],

  // LOINC: 18085-1 | Finding Site: T-32300
  LVIDs: [
    'Left Ventricle Internal Systolic Dimension',          // GE Vivid
    'LVIDs',                                               // Philips EPIQ/Affiniti
    'LV Internal Dimension s',                             // Philips alternate
    'Left Ventricular Internal Diameter at End Systole',   // Philips iE33/CX50 (verbose)
    'LV End-Systolic Internal Diameter',                   // Siemens Acuson
    'LV Internal Dimension - Systole',                     // Canon/Toshiba
    'LVDs',                                                // Mindray / Samsung
    'LV Internal Dimension Systole',                       // Hitachi/Fujifilm
    'Left Ventricular Internal Diameter End Systole',      // DICOM standard alternate
    'LV Internal Diameter Systole',                        // Generic
    'Left Ventricular Internal Systolic Dimension',        // DICOM CID 12227
  ],

  // LOINC: 18157-8 | Finding Site: T-32300
  IVSWd: [
    'Interventricular Septum Diastolic Thickness',             // GE Vivid
    'IVSd',                                                     // Philips EPIQ/Affiniti / Samsung
    'IVS Thickness d',                                          // Philips alternate
    'Interventricular Septum Thickness at End Diastole',        // Philips iE33 (verbose)
    'Interventricular Septal Thickness at End-Diastole',        // Siemens Acuson
    'IVS Thickness - Diastole',                                 // Canon/Toshiba
    'IVSTd',                                                    // Mindray / Samsung / Hitachi
    'IVS Thickness Diastole',                                   // Hitachi/Fujifilm
    'Interventricular Septal Wall Diastolic Thickness',         // DICOM standard
    'Interventricular Septum - Diastole',                       // Canon alternate
  ],

  // LOINC: 18159-4 | Finding Site: T-32300
  LVPWD: [
    'Left Ventricle Posterior Wall Diastolic Thickness',            // GE Vivid
    'LVPWd',                                                        // Philips EPIQ/Affiniti / Samsung
    'LVPW Thickness d',                                             // Philips alternate
    'Left Ventricular Posterior Wall Thickness at End Diastole',    // Philips iE33 (verbose)
    'LV Posterior Wall Thickness at End-Diastole',                  // Siemens Acuson
    'LV Posterior Wall - Diastole',                                 // Canon/Toshiba
    'LVPWTd',                                                       // Mindray / Samsung / Hitachi
    'LVPW Thickness Diastole',                                      // Hitachi/Fujifilm
    'Left Ventricular Posterior Wall Diastolic Thickness',          // DICOM standard
  ],

  // LOINC: 18043-0 | Finding Site: T-32300
  'EF Biplane': [
    'Left Ventricular Ejection Fraction',                  // GE Vivid / DICOM standard
    'LVEF Biplane',                                        // Philips EPIQ
    'LVEF MOD Biplane',                                    // Philips alternate
    'EF (MOD-bp)',                                         // Philips QLAB biplane
    // 'EF (MOD-sp4)',                                        // Philips QLAB single-plane A4C
    // 'EF (MOD-sp2)',                                        // Philips QLAB single-plane A2C
    'EF (Biplane)',                                        // Philips generic
    'Left Ventricular Ejection Fraction by 2D Biplane Method of Disks', // Siemens (DICOM long form)
    'EF (Simpsons)',                                       // Siemens Acuson
    'Ejection Fraction by Simpsons Biplane',               // Canon/Toshiba
    'EF (MOD-Biplane)',                                    // Canon alternate / Hitachi
    'EF(MOD-BP)',                                          // Mindray
    'LVEF(BP)',                                            // Mindray alternate
    'EF(MOD-bp)',                                          // Samsung
    'EF(Biplane)',                                         // Samsung alternate
    'Ejection Fraction (Biplane)',                         // Hitachi/Fujifilm
    'Left Ventricular Ejection Fraction by Simpsons',      // Generic
    'Ejection Fraction',                                   // Short form
    'EF Biplane',                                          // Direct match
    'LVEF',                                                // Samsung/Mindray short
    'Left Ventricular Ejection Fraction by US',            // US2.AI (99IMPROOF) — same LOINC 18043-0, different name
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // LV Tissue Doppler — Diastology
  // ═══════════════════════════════════════════════════════════════════════════

  // LOINC: 59103-6 | Finding Site: T-32600 (LV Lateral Wall) — disambiguated by section
  "E' lateral": [
    'Left Ventricular Peak Early Diastolic Tissue Velocity', // GE Vivid (lateral by section)
    "E' Lateral",                                            // Philips EPIQ
    "E' lat",                                                // Siemens / Philips short
    "e' lat",                                                // Mindray / Samsung lowercase
    'Lat e\'',                                               // Philips alternate
    "Lateral e'",                                            // Philips/Canon alternate
    'Lateral E Prime',                                       // Canon/Toshiba
    'Lateral E\' Velocity',                                  // Canon alternate
    "e' lateral",                                            // lowercase variant
    "Lateral e' Velocity",                                   // Hitachi/Fujifilm
    'Lateral Mitral Annulus e Prime',                         // Hitachi alternate
    'Lateral Mitral Annulus E\' Velocity',                   // DICOM standard long form
    'LV Lateral Wall Peak Early Diastolic Tissue Velocity',  // Generic verbose
    'Peak Tissue Velocity during Early Filling, Lateral',    // Siemens alternate
    'Lateral Mitral Annulus e\' Velocity',                   // Siemens alternate
    "Lateral Mitral Annulus e' Peak Velocity",               // DICOM CID
    'Peak Tissue Velocity',                                  // GE Vivid (LN:59133-9) — under Mitral Valve section
  ],

  // LOINC: 59103-6 | Finding Site: T-32410 (Interventricular Septum) — same LOINC, different site
  "E' sept": [
    'Left Ventricular Peak Early Diastolic Tissue Velocity', // GE Vivid (septal by section)
    "E' Septal",                                             // Philips EPIQ
    "E' sep",                                                // Siemens / Philips short
    "e' sep",                                                // Mindray / Samsung lowercase
    "TVD- E' medial velocity",                               // GE LE10s (GEU-1005-179) — medial = septal
    "E' medial",                                             // GE LE10s generic medial form
    "e' medial",                                             // GE LE10s lowercase
    "E' Medial",                                             // GE LE10s alternate capitalisation
    'Sep e\'',                                               // Philips alternate
    "Septal e'",                                             // Philips/Canon alternate
    'Septal E Prime',                                        // Canon/Toshiba
    'Septal E\' Velocity',                                   // Canon alternate
    "e' septal",                                             // lowercase variant
    "Septal e' Velocity",                                    // Hitachi/Fujifilm
    'Septal Mitral Annulus e Prime',                          // Hitachi alternate
    'Septal Mitral Annulus E\' Velocity',                    // DICOM standard long form
    'LV Septal Wall Peak Early Diastolic Tissue Velocity',   // Generic verbose
    'Peak Tissue Velocity during Early Filling, Septal',     // Siemens alternate
    'Septal Mitral Annulus e\' Velocity',                    // Siemens alternate
    "Septal Mitral Annulus e' Peak Velocity",                // DICOM CID
    'Peak Tissue Velocity',                                  // GE Vivid (LN:59133-9) — under Mitral Valve section
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // LVOT (Left Ventricular Outflow Tract)
  // ═══════════════════════════════════════════════════════════════════════════

  // LOINC: 12144-8 (VTI generic) | Finding Site: T-32602 (LVOT)
  'LVOT VTI': [
    'Velocity Time Integral',                  // GE Vivid (under LV section)
    'LVOT VTI',                                // Philips / Mindray / Samsung
    'VTI LVOT',                                // Philips alternate
    'LV Outflow Tract VTI',                    // Siemens
    'Left Ventricular Outflow Tract Velocity Time Integral', // Siemens (DICOM long)
    'LVOT Velocity Time Integral',             // Canon/Toshiba
    'Left Ventricular Outflow Tract VTI',      // Hitachi/Fujifilm
  ],

  // LOINC: 20167-3 | Finding Site: T-32602
  'LVOT Diam': [
    'Cardiovascular Orifice Diameter',         // GE Vivid (under LV section)
    'LVOT Diameter',                           // Philips / Hitachi
    'LVOT Diam',                               // Siemens / Canon short
    'LVOT D',                                  // Mindray / Samsung
    'LV Outflow Tract Diameter',               // Siemens alternate / Canon
    'Left Ventricular Outflow Tract Diameter',  // DICOM standard
  ],

  // LOINC: 11726-7 | Finding Site: T-32602
  'LVOT Vmax': [
    'Peak Velocity',                           // GE Vivid (under LV section, filtered to 11726-7)
    'LVOT Peak Velocity',                      // Philips
    'LVOT Peak Vel',                           // Philips short
    'LVOT Vmax',                               // Siemens / Canon / Samsung
    'LV Outflow Tract Peak Velocity',          // Siemens alternate / Canon
    'Left Ventricular Outflow Tract Peak Velocity', // DICOM standard / Hitachi
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // Left Atrium
  // ═══════════════════════════════════════════════════════════════════════════

  // LOINC: 18067-9 | Finding Site: T-32100 (Left Atrium)
  'LA Area': [
    'Left Atrium Systolic Area',               // GE Vivid
    'LA Area',                                 // Philips / Mindray / Samsung
    'LA Area A4C',                             // Canon / Philips alternate
    'LA area(A4C)',                             // Samsung alternate
    'Left Atrial Area',                        // Siemens
    'Left Atrial End Systolic Area',           // Siemens alternate / DICOM standard
    'Left Atrium Area',                        // Hitachi / Generic
    'LA Area A4C View',                        // Canon alternate
    'Left Atrium Area A4C view',               // GE Vivid (LN:17977-0)
    'Left Atrium Area on Apical two chamber view', // GE Vivid (99GEMS:GEU-106-0104)
    // GE LE10s: use A4C view only (client-confirmed); A2C intentionally excluded
    'LAAd(A4C)',                               // GE LE10s (GEU-1005-49) — diastolic area 4-chamber
  ],

  // LOINC: 77907-3 | Finding Site: T-32100
  'LA Vol Ind': [
    'Left Atrium Systolic Volume Index',       // GE Vivid
    'LA Volume Index',                         // Philips
    'LA Vol Index',                            // Canon / Hitachi
    'Left Atrial Volume Index',                // Siemens / DICOM standard
    'LAVI',                                    // Philips / Mindray / Samsung / Hitachi short
    'LA Vol/BSA',                              // Mindray alternate
    'LAV/BSA',                                 // Samsung alternate
    'Left Atrial End Systolic Volume Index',   // DICOM standard long
    'Left Atrium Volume Index',                // Generic
    'Left Atrium Systolic Volume Index',       // Philips (99PMSBLUS:C12205-03)
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // Right Atrium
  // ═══════════════════════════════════════════════════════════════════════════

  // LOINC: 18074-5 | Finding Site: T-32200 (Right Atrium)
  'RA Area': [
    'Right Atrium Systolic Area',              // GE Vivid
    'RA Area',                                 // Philips / Mindray / Samsung
    'RA Area A4C',                             // Canon alternate
    'Right Atrial Area',                       // Siemens / DICOM standard
    'Right Atrial End Systolic Area',          // Siemens alternate
    'Right Atrium Area',                       // Hitachi / Generic
    'Right Atrium Area A4C view',              // US2.AI (LOINC 17988-7)
    'Right Atrium Systolic Area',              // Philips (LN:17988-7)
    'Area',                                    // Clinic 2 GE (G-A166) — generic concept, safe here because
    //   RA Area is in SECTION_SPECIFIC_FIELDS so Pass 2 never runs and cross-contamination is prevented
    'RAAd(A4C)',                               // GE LE10s (GEU-1005-64) — diastolic area 4-chamber
    'RAAd',                                    // GE LE10s short form
  ],

  RAP: [
    'Right Atrium Systolic Pressure',          // GE Vivid
    'RAP',                                     // Philips / Samsung
    'Right Atrial Pressure',                   // Siemens / DICOM standard
    'RA Pressure',                             // Canon / Mindray
    'Estimated RAP',                           // Generic alternate
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // Aortic Valve
  // ═══════════════════════════════════════════════════════════════════════════

  // LOINC: 20354-7 | Finding Site: T-35400 (Aortic Valve)
  'AV MG': [
    'Mean Gradient',                           // GE Vivid (under Aortic Valve section)
    'AV Mean Gradient',                        // Philips
    'AV Mean Grad',                            // Philips short
    'Aortic Valve Mean Gradient',              // Siemens / DICOM standard
    'AoV Mean Gradient',                       // Canon
    'AoV Mean PG',                             // Canon alternate
    'AV mean PG',                              // Samsung
    'AV Mean PG',                              // Samsung alternate
    'AV MG',                                   // Mindray
  ],

  // LOINC: 12144-8 | Finding Site: T-35400
  'AV VTI': [
    'Velocity Time Integral',                  // GE Vivid (under Aortic Valve section)
    'AV VTI',                                  // Philips / Canon / Mindray / Samsung
    'Aortic Valve VTI',                        // Siemens
    'AoV VTI',                                 // Canon alternate
    'Aortic Valve Velocity Time Integral',     // DICOM standard / Hitachi
  ],

  // LOINC: 11726-7 | Finding Site: T-35400
  'AV Vmax': [
    'Peak Velocity',                           // GE Vivid (under Aortic Valve section)
    'AV Peak Velocity',                        // Philips
    'AV Peak Vel',                             // Philips short
    'Aortic Valve Peak Velocity',              // Siemens / DICOM standard
    'AV Vmax',                                 // Canon / Mindray / Samsung
    'AoV Vmax',                                // Canon alternate
    'AoV Peak Velocity',                       // Canon alternate
    'Aortic Peak Velocity',                    // Hitachi / Generic
    'AV Peak Vel',                             // Samsung short
  ],

  // LOINC: 17986-1 | Finding Site: T-35400 — max value selection
  'AVA Vmax': [
    'Cardiovascular Orifice Area',             // GE Vivid (max value)
    'AVA by Vmax',                             // Philips
    'Aortic Valve Area (Vmax)',                // Siemens
    'AVA Vmax',                                // Canon / Mindray
    'AVA(Vmax)',                                // Samsung
    'Aortic Valve Area Vmax',                  // Hitachi / Generic
    'AVA',                                     // Mindray/Samsung short (context: Vmax)
    'Aortic Valve Area',                       // DICOM standard
  ],

  // LOINC: 17986-1 | Finding Site: T-35400 — min value selection
  'AVA VTI': [
    'Cardiovascular Orifice Area',             // GE Vivid (min value)
    'AVA by VTI',                              // Philips
    'Aortic Valve Area (VTI)',                 // Siemens
    'AVA VTI',                                 // Canon / Mindray
    'AVA(VTI)',                                // Samsung
    'Aortic Valve Area VTI',                   // Hitachi / Generic
  ],

  // LOINC: 20256-4 | Finding Site: T-35400
  'AV PG': [
    'Peak Gradient',                           // GE Vivid (under Aortic Valve section)
    'AV Peak Gradient',                        // Philips
    'AV Peak Grad',                            // Philips short
    'Aortic Valve Peak Gradient',              // Siemens / DICOM standard
    'AV Peak Instantaneous Gradient',          // Siemens alternate (LOINC: 20280-4)
    'AoV Peak Gradient',                       // Canon
    'AoV Max PG',                              // Canon alternate
    'AV max PG',                               // Samsung
    'AV PG',                                   // Mindray
    'AV PGmax',                                // Mindray alternate
    'Aortic Peak Gradient',                    // Hitachi / Generic
    'AV Peak PG',                              // Hitachi alternate
  ],

  DPI: [
    'DVI',                                     // GE Vivid Calculation section key
    'Aortic Valve Velocity Ratio',             // GE Vivid
    'DPI',                                     // Philips / Mindray / Samsung
    'Dimensionless Performance Index',         // Siemens
    'LVOT/AV VTI Ratio',                       // Siemens alternate
    'Maximum velocity in the LVOT to Maximum velocity in AV', // US2.AI (99IMPROOF)
    // Removed: 'Velocity Ratio' (too generic, matches E/e' ratios in Calculation section)
    // Removed: 'Dimensionless Index' (too generic, ambiguous)
  ],

  'AR PHT': [
    'Pressure Half-Time',                      // GE Vivid (under Aortic Valve section)
    'AR PHT',                                  // Philips / Mindray / Samsung
    'Aortic Regurgitation Pressure Half-Time', // Siemens
    'AR Pressure Half Time',                   // Canon
    'AR Pressure Half-Time',                   // Hitachi
    'Aortic Regurgitation PHT',                // Generic
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // Aortic Root / Sinus of Valsalva
  // ═══════════════════════════════════════════════════════════════════════════

  // LOINC: 18010-9 | Finding Site: T-35401 (Sinus of Valsalva)
  ARD: [
    'Diameter',                                // GE Vivid (under Structure Sinus of Valsalva)
    'Aortic Root Diameter',                    // Philips / DICOM standard
    'Ao Root Diam',                            // Philips alternate
    'Ao Root',                                 // Philips short
    'Sinus of Valsalva Diameter',              // Siemens
    'AoR Diameter',                            // Canon
    'AoR Diam',                                // Canon short
    'Aortic Root',                             // Mindray / Samsung / Hitachi short
    'Ao Root Diameter',                        // Hitachi alternate
    'AoR',                                     // Mindray short
    'SoV',                                     // Generic short
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // Aorta
  // ═══════════════════════════════════════════════════════════════════════════

  // LOINC: 18016-6 | Finding Site: T-42100 (Ascending Aorta)
  'Asc Aorta': [
    'Ascending Aortic Diameter',               // GE Vivid
    'Ascending Aorta Diameter',                // Philips
    'Ascending Aorta',                         // Siemens / Samsung
    'Asc Ao Diameter',                         // Canon / Philips alternate
    'Asc Ao Diam',                             // Canon short / Philips short
    'Asc Ao',                                  // Mindray / Samsung short
    'AAo D',                                   // Samsung alternate
    'AAo',                                     // Samsung short
    'Ascending Aorta Diam',                    // Hitachi
  ],

  Arch: [
    'Aortic Arch Diameter',                    // GE Vivid
    'Aortic Arch',                             // Philips / Samsung
    'Arch Diameter',                           // Siemens / Canon
    'Ao Arch',                                 // Mindray
    'Ao Arch Diameter',                        // Hitachi
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // Aortic Sinotubular Junction
  // ═══════════════════════════════════════════════════════════════════════════

  // LOINC: 77908-1 | Finding Site: T-42070 (Sinotubular Junction)
  'Aorta STJ': [
    'Diameter',                                // GE Vivid (under Aortic Sinotubular Junction)
    'STJ Diameter',                            // Philips / DICOM standard
    'STJ Diam',                                // Philips short
    'Sinotubular Junction Diameter',           // Siemens / DICOM standard long
    'Aorta STJ',                               // Canon
    'STJ',                                     // Mindray / Samsung / Hitachi short
    'STJ D',                                   // Mindray alternate
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // Inferior Vena Cava
  // ═══════════════════════════════════════════════════════════════════════════

  // LOINC: 18070-3 | Finding Site: T-48610 (Inferior Vena Cava)
  IVC: [
    'Inferior Vena Cava Diameter',             // GE Vivid / DICOM standard
    'IVC Diameter',                            // Philips / Hitachi
    'IVC',                                     // Siemens
    'IVC Diam',                                // Canon / Philips short
    'IVC D',                                   // Mindray / Samsung
    'IVC Max Diameter',                        // Siemens alternate
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // Pulmonary Venous Structure
  // ═══════════════════════════════════════════════════════════════════════════

  // LOINC: 20351-3 | Finding Site: T-48500 (Pulmonary Vein)
  'PVein D': [
    'Pulmonary Vein Diastolic Peak Velocity',  // GE Vivid / DICOM standard
    'PV Diastolic Velocity',                   // Philips
    'PV D Velocity',                           // Canon
    'Pulmonary Vein D Wave',                   // Siemens
    'PV D Vel',                                // Mindray / Samsung
    'Pulmonary Vein D Velocity',               // Hitachi
    'PV D',                                    // Short form
  ],

  // LOINC: 20352-1 | Finding Site: T-48500
  'PVein S': [
    'Pulmonary Vein Systolic Peak Velocity',   // GE Vivid / DICOM standard
    'PV Systolic Velocity',                    // Philips
    'PV S Velocity',                           // Canon
    'Pulmonary Vein S Wave',                   // Siemens
    'PV S Vel',                                // Mindray / Samsung
    'Pulmonary Vein S Velocity',               // Hitachi
    'PV S',                                    // Short form
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // Mitral Valve
  // ═══════════════════════════════════════════════════════════════════════════

  // LOINC: 20247-3 | Finding Site: T-35300 (Mitral Valve)
  'Decel time': [
    'Deceleration Time',                       // GE Vivid / DICOM standard
    'DT',                                      // Philips / Samsung
    'MV DT',                                   // Philips alternate / Mindray
    'Decel Time',                              // Siemens
    'MV Deceleration Time',                    // Canon / Siemens alternate
    'Mitral Valve Deceleration Time',          // DICOM standard long
    'Mitral Valve E-Wave Deceleration Time',   // Siemens (LOINC specific)
    'DcT',                                     // Canon short / Mindray / Samsung
    'MV DcT',                                  // Samsung alternate
    'Mitral E Wave Deceleration Time',         // Hitachi
  ],

  // LOINC: 12145-3 | Finding Site: T-35300
  'A vel': [
    'Mitral Valve A-Wave Peak Velocity',       // GE Vivid / DICOM standard
    'MV A Velocity',                           // Philips
    'MV A Vel',                                // Canon / Philips short
    'Mitral A Wave Peak Velocity',             // Siemens
    'MV A',                                    // Mindray / Samsung short
    'A Vel',                                   // Mindray alternate
    'A Velocity',                              // Samsung alternate
    'A Wave Velocity',                         // Hitachi
    'A Wave Peak Velocity',                    // Hitachi alternate
    'MV A Wave Velocity',                      // Hitachi alternate
    'A Peak Velocity',                         // Hitachi alternate
    'MV Peak A Velocity',                      // Siemens alternate
  ],

  // LOINC: 12144-6 | Finding Site: T-35300
  'E vel': [
    'Mitral Valve E-Wave Peak Velocity',       // GE Vivid / DICOM standard
    'MV E Velocity',                           // Philips
    'MV E Vel',                                // Canon / Philips short
    'Mitral E Wave Peak Velocity',             // Siemens
    'MV E',                                    // Mindray / Samsung short
    'E Vel',                                   // Mindray alternate
    'E Velocity',                              // Samsung alternate
    'E Wave Velocity',                         // Hitachi
    'E Wave Peak Velocity',                    // Hitachi alternate
    'MV E Wave Velocity',                      // Hitachi alternate
    'E Peak Velocity',                         // Hitachi alternate
    'MV Peak E Velocity',                      // Siemens alternate
  ],

  // LOINC: 59092-1 | Finding Site: T-35300
  'E/A': [
    'Mitral Valve E to A Ratio',               // GE Vivid
    'E/A Ratio',                               // Philips / Hitachi
    'MV E/A',                                  // Siemens / Samsung
    'E/A',                                     // Canon / Mindray short
    'E-Wave to A-Wave Peak Velocity Ratio',    // DICOM standard long
    'MV E/A Ratio',                            // Siemens alternate
    'Mitral valve E-to-A ratio',               // US2.AI (LOINC 18038-0, lowercase + hyphenated)
  ],

  // LOINC: 20354-7 | Finding Site: T-35300
  'MV MG': [
    'Mean Gradient',                           // GE Vivid (under Mitral Valve section)
    'MV Mean Gradient',                        // Philips / Siemens
    'Mitral Valve Mean Gradient',              // Siemens alternate / DICOM standard
    'MV MG',                                   // Canon / Mindray / Samsung
    'MV Mean PG',                              // Samsung alternate
    'MV Mean Grad',                            // Philips short
  ],

  // LOINC: 20280-4 | Finding Site: T-35300
  PHT: [
    'Pressure Half-Time',                      // GE Vivid (under Mitral Valve section) / DICOM standard
    'MV PHT',                                  // Philips / Mindray / Samsung
    'Mitral Valve Pressure Half-Time',         // Siemens / DICOM standard long
    'MV Pressure Half Time',                   // Canon
    'Mitral Valve PHT',                        // Hitachi
    'Mitral PHT',                              // Generic
  ],

  // LOINC: 17986-1 | Finding Site: T-35300
  'MVA (PHT)': [
    'Cardiovascular Orifice Area',             // GE Vivid (under Mitral Valve section)
    'MVA by PHT',                              // Philips
    'MVA (PHT)',                               // Philips alternate
    'Mitral Valve Area (PHT)',                 // Siemens
    'MVA PHT',                                 // Canon / Samsung
    'MVA(PHT)',                                // Mindray
    'Mitral Valve Area',                       // DICOM standard / Hitachi
    'MVA',                                     // Mindray / Samsung short
    'Mitral Valve Area by PHT',                // Siemens alternate
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // Pulmonic Valve
  // ═══════════════════════════════════════════════════════════════════════════

  // LOINC: 11726-7 | Finding Site: T-35200 (Pulmonic Valve)
  'PV Vmax': [
    'Peak Velocity',                           // GE Vivid (under Pulmonic Valve section)
    'PV Peak Velocity',                        // Philips
    'Pulmonic Valve Peak Velocity',            // Siemens / DICOM standard
    'PV Vmax',                                 // Canon / Mindray / Samsung
    'Pulmonary Valve Peak Velocity',           // Hitachi
    'PV Peak Vel',                             // Philips short
  ],

  // LOINC: 20256-4 | Finding Site: T-35200
  'PV PG': [
    'Peak Gradient',                           // GE Vivid (under Pulmonic Valve section)
    'PV Peak Gradient',                        // Philips
    'Pulmonic Valve Peak Gradient',            // Siemens / DICOM standard
    'PV PG',                                   // Canon / Mindray / Samsung
    'Pulmonary Valve Peak Gradient',           // Hitachi
    'PV PGmax',                                // Mindray alternate
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // Right Ventricle
  // ═══════════════════════════════════════════════════════════════════════════

  // LOINC: 18089-3 | Finding Site: T-32400 (Right Ventricle)
  'RV Base': [
    'Right Ventricle Basal Diameter',          // GE Vivid
    'RV Basal Diameter',                       // Philips
    'RV Basal Diam',                           // Philips short
    'RV Base Diameter',                        // Siemens
    'RV Basal Linear Dimension',               // Siemens alternate
    'RV Basal',                                // Canon
    'RV Basal Dimension',                      // Canon alternate
    'RVDd',                                    // Mindray / Samsung / Hitachi short
    'RV D1',                                   // Mindray alternate
    'RV Diam 1',                               // Samsung alternate
    'RVID Basal',                              // Hitachi alternate
    'Right Ventricular Basal Diameter',        // DICOM standard
    'Right ventricular basal dimension 4C',    // US2.AI (LOINC 80080-5, shorter name)
    'Right ventricular base Minor Axis at end diastole, 4-chamber view', // GE Vivid (LN:80080-5, verbose)
  ],

  // LOINC: 59131-7 | Finding Site: T-32400
  "RVS'": [
    'Right Ventricle S Velocity',              // GE Vivid
    "RV S'",                                   // Philips / Samsung
    "RV s'",                                   // Philips lowercase / Samsung
    'RV S Prime',                              // Siemens
    "RV S' Velocity",                          // Canon
    "TV S'",                                   // Philips alternate (under Tricuspid)
    "TV s'",                                   // Samsung alternate
    'Tricuspid S\' Velocity',                  // Canon alternate
    'Tricuspid Annulus Peak Systolic Tissue Velocity',     // DICOM standard / Hitachi
    'Tricuspid Annular Peak Systolic Velocity',            // Siemens alternate
    'Tricuspid Annulus S\' Velocity',          // Hitachi alternate
    'Right Ventricular Peak Systolic Tissue Velocity',     // DICOM CID (LOINC: 59131-7)
    "RV S' Vel",                               // Mindray
    'Peak Tissue Velocity',                    // GE Vivid (LN:59133-9) — generic name, disambiguated by Right Ventricle section
    "TVD- S' velocity",                        // GE LE10s (Tricuspid Valve section) — client-confirmed
  ],

  // LOINC: 12144-8 | Finding Site: T-32550 (RVOT)
  'RVOT VTI': [
    'Velocity Time Integral',                  // GE Vivid (under Right Ventricle section)
    'RVOT VTI',                                // Philips / Mindray / Samsung
    'RV Outflow Tract VTI',                    // Siemens
    'RVOT Velocity Time Integral',             // Canon
    'Right Ventricular Outflow Tract VTI',     // Hitachi / DICOM standard
  ],

  // LOINC: 18091-9 | Finding Site: T-32400
  RVSP: [
    'Right Ventricular Peak Systolic Pressure', // GE Vivid
    'RVSP',                                     // Philips / Mindray / Samsung / Hitachi
    'RV Systolic Pressure',                     // Siemens
    'Right Ventricular Systolic Pressure',      // Canon / DICOM standard
    'Estimated RVSP',                           // Siemens alternate / Hitachi alternate
    'PASP',                                     // Mindray alternate (Pulmonary Artery Systolic Pressure)
    'Estimated RV Systolic Pressure',           // Generic
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // Tricuspid Valve
  // ═══════════════════════════════════════════════════════════════════════════

  // SNOMED: G-0390 (SRT) | Finding Site: T-35100 or T-32400
  TAPSE: [
    'Tricuspid Annular Plane Systolic Excursion', // GE Vivid / DICOM standard
    'TAPSE',                                      // Philips / Mindray / Samsung / Hitachi universal
    'Tricuspid Annulus Plane Systolic Excursion',  // Siemens (slight variation)
    'TA Plane Systolic Excursion',                 // Canon
    'Tricuspid Annulus Systolic Excursion',         // Canon alternate
    'Tricuspid Annular Plane Systolic Excursion (TAPSE)', // GE Vivid (99GEMS GEU-106-0030, with suffix)
  ],

  // LOINC: 11726-7 | Finding Site: T-35100 (Tricuspid Valve)
  'TR Vmax': [
    'Peak Velocity',                               // GE Vivid (under Tricuspid Valve section)
    'TR Peak Velocity',                            // Philips
    'TR Peak Vel',                                 // Philips short
    'Tricuspid Regurgitation Peak Velocity',       // Siemens / DICOM standard
    'TR Vmax',                                     // Canon / Mindray / Samsung
    'Tricuspid Valve Regurgitant Peak Velocity',   // Siemens alternate (LOINC: 20296-0)
    'Tricuspid Valve Peak Velocity',               // Hitachi
    'TV Peak Velocity',                            // Hitachi alternate
  ],

  // LOINC: 20256-4 | Finding Site: T-35100
  'TR PG': [
    'Peak Gradient',                               // GE Vivid (under Tricuspid Valve section)
    'TR Peak Gradient',                            // Philips
    'TR Peak Grad',                                // Philips short
    'Tricuspid Regurgitation Peak Gradient',       // Siemens / DICOM standard
    'TR PG',                                       // Canon / Mindray / Samsung
    'Tricuspid Valve Peak Gradient',               // Siemens alternate / Hitachi
    'TR Peak Instantaneous Gradient',              // Siemens (LOINC specific)
    'TV Peak Gradient',                            // Hitachi alternate
    'TR PGmax',                                    // Mindray alternate
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // Cardiac Shunt Study
  // ═══════════════════════════════════════════════════════════════════════════

  'Qp:Qs': [
    'Pulmonary-to-Systemic Shunt Flow Ratio',     // GE Vivid
    'Qp/Qs',                                       // Philips / Samsung
    'Qp:Qs',                                       // Siemens / Mindray
    'Pulmonary to Systemic Flow Ratio',            // Canon / DICOM standard
    'Qp/Qs Ratio',                                 // Hitachi
    'Shunt Ratio',                                 // Generic
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // Raw values used as inputs for derived calculations
  // These are NOT displayed directly — they feed into LVMI, LVEDVI, LVESVI, LA Vol Ind
  // ═══════════════════════════════════════════════════════════════════════════

  // LOINC: 18037-2 | Finding Site: T-32100 (Left Atrium)
  // _LA_VOL_RAW feeds into LA Vol Ind = LAEDV / BSA
  // GE LE10s reports diastolic LA volume only (no systolic); use biplane summary value.
  // NOTE: do NOT add 'LAEDV' short form — it matches sub-components (A2C, A4C individually)
  // which would pollute the last-value strategy. Use the full summary concept name only.
  _LA_VOL_RAW: [
    'Left Atrium Systolic Volume',             // GE Vivid (other models — systolic biplane)
    'Left Atrial End Diastolic Volume',        // GE LE10s (LOINC 122407) — diastolic biplane summary
    'Left Atrial End Systolic Volume',         // Siemens / DICOM standard
    'LA Volume',                               // Philips / Mindray
    'LA Vol',                                  // Short form
    'LAESV',                                   // Philips / Samsung abbreviation
    'LAV(MOD-BP)',                             // Samsung biplane MOD
    'LA Volume (Biplane)',                     // Generic biplane
  ],

  // LOINC: 18026-5 | Finding Site: T-32300 (Left Ventricle)
  // _LVEDV_RAW is used in requiredMeasurementsJSON for derived calculations
  _LVEDV_RAW: [
    'Left Ventricular End Diastolic Volume',
    'LVEDV',
    'LV End Diastolic Volume',
    'EDV (Biplane)',
    'EDV (BP)',
    'EDV(MOD-BP)',
    'LV EDV',
    'Left Ventricular End-Diastolic Volume',
  ],
  LVEDV: [
    'Left Ventricular End Diastolic Volume',           // GE Vivid / DICOM standard
    'LVEDV',                                           // Philips / Mindray / Samsung
    'LV End Diastolic Volume',                         // Siemens / Canon
    'EDV (Biplane)',                                   // GE alternate
    'EDV (BP)',                                        // GE alternate short
    'EDV(MOD-BP)',                                     // Samsung
    'LV EDV',                                          // Siemens / Hitachi
    'Left Ventricular End-Diastolic Volume',           // Siemens hyphenated
  ],

  // LOINC: 18043-6 | Finding Site: T-32300
  _LVESV_RAW: [
    'Left Ventricular End Systolic Volume',
    'LVESV',
    'LV End Systolic Volume',
    'ESV (Biplane)',
    'ESV (BP)',
    'ESV(MOD-BP)',
    'LV ESV',
    'Left Ventricular End-Systolic Volume',
  ],
  LVESV: [
    'Left Ventricular End Systolic Volume',            // GE Vivid / DICOM standard
    'LVESV',                                           // Philips / Mindray / Samsung
    'LV End Systolic Volume',                          // Siemens / Canon
    'ESV (Biplane)',                                   // GE alternate
    'ESV (BP)',                                        // GE alternate short
    'ESV(MOD-BP)',                                     // Samsung
    'LV ESV',                                          // Siemens / Hitachi
    'Left Ventricular End-Systolic Volume',            // Siemens hyphenated
  ],

  // LOINC: 18087-7 | Finding Site: T-32300
  '_LV_MASS_RAW': [
    'Left Ventricle Mass',
    'LV Mass',
    'Left Ventricular Mass',
    'LVM',
    'LV Mass (2D)',
  ],
  'LV Mass': [
    'Left Ventricle Mass',                             // GE Vivid
    'LV Mass',                                         // Philips / Siemens / Canon / Mindray / Samsung
    'Left Ventricular Mass',                           // DICOM standard / Hitachi
    'LVM',                                             // Mindray / Samsung short
    'LV Mass (2D)',                                    // GE alternate
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // Calculations (User-defined concepts / Derived values)
  // ═══════════════════════════════════════════════════════════════════════════

  // LOINC: 79963-4 | Finding Site: T-32300
  LVEDVI: [
    'EDV (BP) BSA',                            // GE Vivid
    'LVEDVI',                                  // Philips / Mindray / Samsung
    'LV EDV Index',                            // Siemens
    'LV End Diastolic Volume Index',           // Canon / DICOM standard
    'Left Ventricular End Diastolic Volume Index', // DICOM standard long
    'EDV Index',                               // Hitachi
    'LVEDV Index',                             // Hitachi alternate
  ],

  // LOINC: 18055-4 | Finding Site: T-32300
  LVMI: [
    'LV MI (2D)',                              // GE Vivid
    'LVMI',                                    // Philips / Mindray / Samsung
    'LV Mass Index',                           // Siemens / Canon
    'Left Ventricular Mass Index',             // DICOM standard / Hitachi
    'LV Mass/BSA',                             // Mindray alternate
    'LVM Index',                               // Hitachi alternate
  ],

  // LOINC: 79964-2 | Finding Site: T-32300
  LVESVI: [
    'ESV (BP) BSA',                            // GE Vivid
    'LVESVI',                                  // Philips / Mindray / Samsung
    'LV ESV Index',                            // Siemens
    'LV End Systolic Volume Index',            // Canon / DICOM standard
    'Left Ventricular End Systolic Volume Index', // DICOM standard long
    'ESV Index',                               // Hitachi
    'LVESV Index',                             // Hitachi alternate
  ],

  // Finding Site: Calculation section (vendor-specific)
  "Avg E/E'": [
    'E L AVERAGE',                             // GE Vivid
    "Average E/E'",                            // Philips
    "E/e' avg",                                // Philips alternate (lowercase e')
    "E/E' Avg",                                // Philips alternate
    "Avg E/e'",                                // Siemens
    "E/e' Average",                            // Siemens alternate
    "E/E' Average",                            // Canon
    "E/E' average",                            // Samsung
    "Average E/e'",                            // Mindray
    "E/e' Mean",                               // Hitachi
    "Avg E/E'",                                // Direct match
    "Mean E/e'",                               // Generic
    'Ratio of MV Peak Velocity to LV Peak Tissue Velocity E-Wave', // US2.AI (SRT G-037B)
    'E Velocity to Average Annulus E Velocity',              // GE Vivid (99GEMS:GEU-106-0032) — under Mitral Valve
    // GE LE10s: no combined Avg E/E' — reports individual lateral & medial ratios;
    // resolveMatchedValue averages both matches to compute Avg E/E'
    "TVD- Mitral Valve E velocity/E' Lateral ratio",         // GE LE10s (GEU-1005-184) lateral E/E'
    "TVD- Mitral Valve E velocity/E' Medial ratio",          // GE LE10s (GEU-1005-186) medial E/E'
  ],
};

// ═══════════════════════════════════════════════════════════════════════════════
// Finding Site Aliases
// ═══════════════════════════════════════════════════════════════════════════════
// Key   = canonical finding site name (used as lookup key in extracted data)
// Value = array of known finding site names across vendors
//
// SNOMED-CT (SRT/SCT) codes are included in comments for reference.
// This replaces the hardcoded corrections in filterMeasurements().
//
// Note: Mindray and Samsung often use abbreviated finding sites (LV, RV, AoV, MV, TV).
// Philips may nest tissue Doppler (E', S') under valve finding sites instead of chamber.

export const findingSiteAliases: Record<string, string[]> = {
  // SRT: T-32300
  'Left Ventricle': [
    'Left Ventricle',
    'Left Ventrical',            // Typo in GE Vivid SRs
    'Left ventricle',
    'LV',                        // Mindray / Samsung abbreviated
    'Left Ventricular',          // Alternate adjective form
    'Left ventricular structure', // SNOMED-CT full term
  ],
  // SRT: T-32100
  'Left Atrium': [
    'Left Atrium',
    'Left atrium',
    'LA',                        // Mindray / Samsung abbreviated
    'Left Atrial',               // Adjective form
  ],
  // SRT: T-32200
  'Right Atrium': [
    'Right Atrium',
    'Right atrium',
    'RA',                        // Mindray / Samsung abbreviated
    'Right Atrial',              // Adjective form
  ],
  // SRT: T-35400
  'Aortic Valve': [
    'Aortic Valve',
    'Aortic valve',
    'AoV',                       // Canon / Mindray / Samsung
    'AV',                        // Some systems (caution: also abbreviation for other things)
    'Aortic',                    // Short form
  ],
  // SRT: T-35401
  'Structure Sinus of Valsalva': [
    'Structure Sinus of Valsalva',
    'Sinus of Valsalva',
    'Aortic Root',               // Philips / Mindray / Samsung / Hitachi
    'SoV',                       // Short form
    'Ao Root',                   // Mindray short
    'Aortic root',               // lowercase variant
  ],
  // SRT: T-42000
  Aorta: [
    'Aorta',
    'Ascending Aorta',           // Some vendors separate this
    'Thoracic Aorta',
    'Aorta Ascending',           // Alternate ordering
  ],
  // SRT: T-48610
  'Inferior Vena Cava': [
    'Inferior Vena Cava',
    'Inferior vena cava',        // lowercase variant (seen in some GE SRs)
    'IVC',                       // Mindray / Samsung abbreviated
  ],
  // Alternate finding site for IVC
  'Vena Cava': [
    'Vena Cava',
    'IVC',
    'Inferior Vena Cava',
    'Inferior vena cava',
  ],
  // SRT: T-48500
  'Pulmonary Venous Structure': [
    'Pulmonary Venous Structure',
    'Pulmonary Vein',
    'Pulmonary Veins',
    'Pulmonary vein',
    'PV',                        // Mindray (caution: also Pulmonic Valve)
    'Pulmonary Venous',          // Short form
  ],
  // SRT: T-35300
  'Mitral Valve': [
    'Mitral Valve',
    'Mitral valve',
    'MV',                        // Mindray / Samsung abbreviated
    'Mitral',                    // Short form
  ],
  // SRT: T-35200
  'Pulmonic Valve': [
    'Pulmonic Valve',
    'Pulmonary Valve',           // More common clinical term
    'Pulmonic valve',
    'PV Valve',                  // Some systems
    'Pulmonic',                  // Short form
    'Pulmonary valve',           // lowercase
  ],
  // SRT: T-42070
  'Aortic Sinotubular Junction': [
    'Aortic Sinotubular Junction',
    'Sinotubular Junction',
    'STJ',                       // Universal abbreviation
    'Sinotubular junction',      // lowercase
  ],
  // SRT: T-32400
  'Right Ventricle': [
    'Right Ventricle',
    'Right ventricle',
    'RV',                        // Mindray / Samsung abbreviated
    'Right Ventricular',         // Adjective form
  ],
  'Cardiac Shunt Study': [
    'Cardiac Shunt Study',
    'Shunt Study',
    'Cardiac Shunt',
    'Shunt',                     // Short form
  ],
  // SRT: T-35100
  'Tricuspid Valve': [
    'Tricuspid Valve',
    'Tricuspid valve',
    'TV',                        // Mindray / Samsung abbreviated
    'Tricuspid',                 // Short form
    'Tricuspid Annulus',         // Philips — TAPSE reported under this finding site
  ],
  // Vendor-specific section for calculated/derived values
  Calculation: [
    'Calculation',
    'Calculations',
    'User-defined concepts',     // GE Vivid specific
    'Derived Measurements',      // Siemens alternate
    'Calculated Values',         // Generic
  ],
};

// ─── Helper: Build reverse lookup for finding sites ──────────────────────────
// Maps any alias → canonical name, for quick lookups in filterMeasurements()

let _findingSiteReverseLookup: Record<string, string> | null = null;

export function resolveFindingSite(siteName: string): string {
  if (!_findingSiteReverseLookup) {
    _findingSiteReverseLookup = {};
    for (const [canonical, aliases] of Object.entries(findingSiteAliases)) {
      for (const alias of aliases) {
        _findingSiteReverseLookup[alias] = canonical;
      }
    }
  }
  return _findingSiteReverseLookup[siteName] || siteName;
}

// ─── Helper: Get aliases for a measurement short name ────────────────────────

export function getAliases(shortName: string): string[] {
  return measurementAliases[shortName] || [];
}
